import React from "react";
import { C } from "../../constants/design";
import { CARDS, TIER_META } from "../../constants/cards";
import { generateBotTeam, TeamStanding } from "../../engine/leagueEngine";
import { LeagueMember } from "../../types/game";

interface LeagueTeamDetailProps {
  team: TeamStanding; // ligne du classement cohérent (bilan + fixtures)
  // Joueurs réels de MON équipe (fournis quand team.isMe) — sinon on génère
  // un effectif déterministe pour les bots.
  myMembers?: LeagueMember[];
  onBack: () => void;
}

/** Sélection déterministe de cartes pour le board d'un bot (cohérente par seed). */
function botBoard(seed: number, count = 2) {
  const out: typeof CARDS = [];
  const used = new Set<number>();
  let s = (seed % CARDS.length) + 1;
  while (out.length < count && used.size < CARDS.length) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const idx = s % CARDS.length;
    if (!used.has(idx)) {
      used.add(idx);
      out.push(CARDS[idx]);
    }
  }
  return out;
}

const RESULT_META: Record<"win" | "draw" | "loss", { label: string; color: string }> = {
  win: { label: "V", color: C.turf },
  draw: { label: "N", color: C.gold },
  loss: { label: "D", color: C.live },
};

export function LeagueTeamDetail({ team, myMembers, onBack }: LeagueTeamDetailProps) {
  const seed = team.seed ?? 0;
  const members = React.useMemo(
    () =>
      team.isMe && myMembers && myMembers.length > 0
        ? myMembers.map((m) => ({ name: m.name, power: m.power }))
        : generateBotTeam(1, seed).members,
    [team.isMe, myMembers, seed]
  );
  const board = botBoard(seed);

  // Les matchs déjà joués en premier (les plus récents en haut).
  const playedFixtures = team.fixtures
    .filter((f) => f.due)
    .sort((a, b) => b.round - a.round);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <BackBtn label="← Standings" onClick={onBack} />

      {/* En-tête équipe */}
      <div
        style={{
          background: "linear-gradient(160deg,#1a0e0e,#1a0a0a)",
          border: "1.5px solid rgba(255,59,71,0.18)",
          borderRadius: 20,
          padding: "18px 16px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: team.isMe ? "rgba(45,227,107,0.7)" : "rgba(255,59,71,0.6)",
                letterSpacing: 2,
                textTransform: "uppercase",
                marginBottom: 3,
              }}
            >
              {team.isMe ? "My Team" : "Opponent"}
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: -0.5 }}>
              {team.name}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div
              style={{
                fontSize: 11,
                color: "rgba(255,255,255,0.35)",
                marginBottom: 2,
              }}
            >
              {members.length} players
            </div>
            <div style={{ fontSize: 16, fontWeight: 900, color: C.live }}>
              ⚡ {team.power} PWR
            </div>
          </div>
        </div>

        {/* Bilan de la semaine (round-robin de la poule) */}
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          {[
            { k: "W", v: team.wins, c: C.turf },
            { k: "D", v: team.draws, c: C.gold },
            { k: "L", v: team.losses, c: C.live },
            { k: "PTS", v: team.points, c: C.text },
          ].map((s) => (
            <div
              key={s.k}
              style={{
                flex: 1,
                background: "rgba(0,0,0,0.25)",
                borderRadius: 12,
                padding: "10px 0",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 20, fontWeight: 900, color: s.c }}>
                {s.v}
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: "rgba(255,255,255,0.35)",
                  letterSpacing: 1,
                }}
              >
                {s.k}
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            fontSize: 10.5,
            color: "rgba(255,255,255,0.3)",
            marginTop: 8,
            textAlign: "center",
          }}
        >
          Record this week
        </div>
      </div>

      {/* Cartes sur leur board */}
      <Section title="🃏 Board">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: 12 }}>
          {board.map((card) => (
            <div
              key={card.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: `${TIER_META[card.tier].color}14`,
                border: `1px solid ${TIER_META[card.tier].color}55`,
                borderRadius: 10,
                padding: "8px 11px",
              }}
            >
              <span style={{ fontSize: 18 }}>{card.icon}</span>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: C.text }}>
                  {card.name}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: TIER_META[card.tier].color,
                  }}
                >
                  {TIER_META[card.tier].label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Joueurs */}
      <Section title="👥 Players">
        {members.map((m, i) => (
          <Row key={i} last={i === members.length - 1}>
            <span
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: "rgba(255,255,255,0.25)",
                width: 18,
                textAlign: "center",
              }}
            >
              #{i + 1}
            </span>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.text }}>
              {m.name}
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.muted }}>
              ⚡ {m.power}
            </span>
          </Row>
        ))}
      </Section>

      {/* Historique de la semaine (toutes ses confrontations) */}
      <Section title="📜 This week's results">
        {playedFixtures.length === 0 ? (
          <div
            style={{
              padding: 16,
              textAlign: "center",
              fontSize: 12.5,
              color: C.muted,
            }}
          >
            No match played yet this week.
          </div>
        ) : (
          playedFixtures.map((f, i) => {
            const meta = RESULT_META[f.result as "win" | "draw" | "loss"];
            return (
              <Row key={i} last={i === playedFixtures.length - 1}>
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    background: `${meta.color}1F`,
                    color: meta.color,
                    fontSize: 11,
                    fontWeight: 900,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                  }}
                >
                  {meta.label}
                </span>
                <span
                  style={{
                    flex: 1,
                    fontSize: 12.5,
                    color: f.opponentIsMe ? C.turf : C.muted,
                    fontWeight: f.opponentIsMe ? 800 : 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  vs {f.opponentIsMe ? "You" : f.opponentName}
                </span>
                <span style={{ fontSize: 13, fontWeight: 800, color: C.text }}>
                  {f.goalsFor}–{f.goalsAgainst}
                </span>
              </Row>
            );
          })
        )}
      </Section>
    </div>
  );
}

/* ─── Sous-composants UI ──────────────────────────────────────────────────── */
function BackBtn({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        alignSelf: "flex-start",
        background: "transparent",
        border: "none",
        color: C.muted,
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer",
        padding: "4px 0",
      }}
    >
      {label}
    </button>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          color: "rgba(255,255,255,0.35)",
          letterSpacing: 2,
          textTransform: "uppercase",
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      <div
        style={{
          background: C.surface,
          borderRadius: 14,
          border: "1px solid rgba(255,255,255,0.06)",
          overflow: "hidden",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Row({ children, last }: { children: React.ReactNode; last?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "11px 14px",
        borderBottom: last ? "none" : "1px solid rgba(255,255,255,0.04)",
      }}
    >
      {children}
    </div>
  );
}
